import React, { useState, useEffect } from "react";
import {
  Dialog,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AlertTriangle, X, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { deleteSlab } from "../../../services/slabs";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "90%",
      maxWidth: "100%",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
      [theme.breakpoints.up("sm")]: { width: "400px" },
    },
  };
});

export default function DeleteSlabModal({ open, onClose, refetch, slabData }) {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const deleteSlabMutation = useMutation({
    mutationFn: deleteSlab,
    onSuccess: () => {
      toast.success(t('deleteSlab.success') || "Slab deleted successfully");
      refetch();
      onClose();
    },
    onError: (error) => {
      const message = error?.response?.data?.error || t('deleteSlab.error') || "Failed to delete slab";
      toast.error(message);
    },
  });

  const handleDelete = async () => {
    if (!slabData?.id) return;
    
    await deleteSlabMutation.mutateAsync(slabData.id);
  };

  const loading = deleteSlabMutation.isPending;

  return (
    <StyledDialog open={open} onClose={loading ? undefined : onClose}>
      <div className="px-5 py-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-500">
            {t('deleteSlab.title') || "Delete Slab"}
          </h3>
          {!loading && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>

        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isDark ? 'bg-red-900/30' : 'bg-red-100'
            }`}>
              <AlertTriangle className={`${isDark ? 'text-red-500' : 'text-red-600'}`} size={32} />
            </div>
          </div>
          
          <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('deleteSlab.confirmTitle') || "Are you sure?"}
          </h4>
          
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('deleteSlab.confirmMessage') || "You are about to delete this slab. This action cannot be undone."}
          </p>

          {slabData && (
            <div className={`rounded-md p-3 mb-4 ${
              isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="text-left">
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {slabData.slabTypeDetails?.title}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('deleteSlab.percentage') || "Percentage"}: <strong className={isDark ? 'text-white' : 'text-gray-900'}>
                    {parseFloat(slabData.percentage).toFixed(2)}%
                  </strong>
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('deleteSlab.for') || "For"}: <strong className={isDark ? 'text-white' : 'text-gray-900'}>
                    {t(`slabFor.${slabData.slabFor}`) || slabData.slabFor}
                  </strong>
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('deleteSlab.created') || "Created"}: <strong className={isDark ? 'text-white' : 'text-gray-900'}>
                    {new Date(slabData.createdAt).toLocaleDateString()}
                  </strong>
                </p>
              </div>
            </div>
          )}

          <p className={`text-sm font-semibold ${isDark ? 'text-red-500' : 'text-red-600'}`}>
            {t('deleteSlab.warning') || "This will permanently remove the slab from the system."}
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`py-2 px-6 border rounded-md transition-colors disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-400 text-gray-700 hover:bg-gray-50'
            }`}
            disabled={loading}
          >
            {t('common.cancel') || "Cancel"}
          </button>
          <button
            onClick={handleDelete}
            className={`text-white bg-red-600 hover:bg-red-700 py-2 px-6 rounded-md disabled:bg-gray-400 dark:disabled:bg-gray-600 flex items-center gap-2 transition-colors ${
              isDark ? 'dark:bg-red-700 dark:hover:bg-red-800' : ''
            }`}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <Trash2 size={18} />
                {t('deleteSlab.deleteButton') || "Delete Slab"}
              </>
            )}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}